import api from './api';

/**
 * Prepares the data for the tailor API from the component state
 * @param {Object} tailoringData - The internal component state data
 * @returns {Object} - Formatted data for the API request
 */
export const prepareTailorRequestData = (tailoringData) => {
  // Create the base request object that matches the API structure
  const apiRequestData = {
    job_description: tailoringData.jobDescription || "",
    use_profile: true,
    profile_selections: {
      excluded_sections: [],
      temporary_items: []
    },
    file_type: "pdf",
    file_content: null,
    additional_info: tailoringData.additionalInfo || ""
  };

  // Optional fields
  if (tailoringData.jobTitle) {
    apiRequestData.job_title = tailoringData.jobTitle;
  }

  if (tailoringData.company) {
    apiRequestData.company = tailoringData.company;
  }

  // Handle excluded sections
  if (!tailoringData.selectedEducation || tailoringData.selectedEducation.length === 0) {
    apiRequestData.profile_selections.excluded_sections.push("education");
  }
  
  if (!tailoringData.selectedSkills || tailoringData.selectedSkills.length === 0) {
    apiRequestData.profile_selections.excluded_sections.push("skills");
  }
  
  if (!tailoringData.selectedExperience || tailoringData.selectedExperience.length === 0) {
    apiRequestData.profile_selections.excluded_sections.push("experiences");
  }
  
  if (!tailoringData.selectedProjects || tailoringData.selectedProjects.length === 0) {
    apiRequestData.profile_selections.excluded_sections.push("projects");
  }
  
  if (!tailoringData.selectedCertificates || tailoringData.selectedCertificates.length === 0) {
    apiRequestData.profile_selections.excluded_sections.push("certificates");
  }
  
  if (!tailoringData.selectedExtraCurricular || tailoringData.selectedExtraCurricular.length === 0) {
    apiRequestData.profile_selections.excluded_sections.push("extracurricular_activities");
  }
  
  if (!tailoringData.selectedCustomSections || tailoringData.selectedCustomSections.length === 0) {
    apiRequestData.profile_selections.excluded_sections.push("custom_sections");
  }

  // Process profile data to identify excluded items within each section
  const profileData = tailoringData.profileData;
  if (profileData) {
    // Process education items
    if (Array.isArray(profileData.education) && Array.isArray(tailoringData.selectedEducation)) {
      profileData.education.forEach(item => {
        if (!tailoringData.selectedEducation.includes(item.id)) {
          apiRequestData.profile_selections.temporary_items.push({
            type: "education",
            id: item.id,
            action: "exclude"
          });
        }
      });
    }
    
    // Process skill categories
    if (Array.isArray(profileData.skill_categories) && Array.isArray(tailoringData.selectedSkills)) {
      profileData.skill_categories.forEach(category => {
        if (!tailoringData.selectedSkills.includes(category.id)) {
          apiRequestData.profile_selections.temporary_items.push({
            type: "skill_category",
            id: category.id,
            action: "exclude"
          });
        }
      });
    }
    
    // Process experience items
    if (Array.isArray(profileData.experiences) && Array.isArray(tailoringData.selectedExperience)) {
      profileData.experiences.forEach(item => {
        if (!tailoringData.selectedExperience.includes(item.id)) {
          apiRequestData.profile_selections.temporary_items.push({
            type: "experience",
            id: item.id,
            action: "exclude"
          });
        }
      });
    }
    
    // Process project items
    if (Array.isArray(profileData.projects) && Array.isArray(tailoringData.selectedProjects)) {
      profileData.projects.forEach(item => {
        if (!tailoringData.selectedProjects.includes(item.id)) {
          apiRequestData.profile_selections.temporary_items.push({
            type: "project",
            id: item.id,
            action: "exclude"
          });
        }
      });
    }
    
    // Process certificate items
    if (Array.isArray(profileData.certificates) && Array.isArray(tailoringData.selectedCertificates)) {
      profileData.certificates.forEach(item => {
        if (!tailoringData.selectedCertificates.includes(item.id)) {
          apiRequestData.profile_selections.temporary_items.push({
            type: "certificate",
            id: item.id,
            action: "exclude"
          });
        }
      });
    }
    
    // Process extracurricular items
    if (Array.isArray(profileData.extracurricular_activities) && Array.isArray(tailoringData.selectedExtraCurricular)) {
      profileData.extracurricular_activities.forEach(item => {
        if (!tailoringData.selectedExtraCurricular.includes(item.id)) {
          apiRequestData.profile_selections.temporary_items.push({
            type: "extracurricular",
            id: item.id,
            action: "exclude"
          });
        }
      });
    }
    
    // Process custom section items
    if (Array.isArray(profileData.custom_sections) && Array.isArray(tailoringData.selectedCustomSections)) {
      profileData.custom_sections.forEach(item => {
        if (!tailoringData.selectedCustomSections.includes(item.id)) {
          apiRequestData.profile_selections.temporary_items.push({
            type: "custom_section",
            id: item.id,
            action: "exclude"
          });
        }
      });
    }
  }

  // Include any custom content as part of the additional info (as JSON string)
  if (tailoringData.customContent && Object.keys(tailoringData.customContent).length > 0) {
    apiRequestData.additional_info = JSON.stringify(tailoringData.customContent);
  }

  return apiRequestData;
};


// Submit a tailoring job
export const submitTailoringJob = async (tailoringData) => {
  try {
    const response = await api.post('/tailor/process', prepareTailorRequestData(tailoringData));
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Check tailoring job status
export const getTailoringStatus = async (jobId) => {
  try {
    const response = await api.get(`/tailor/status/${jobId}`);
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
};

// // Get tailoring results
// export const getTailoringResult = async (jobId) => {
//   try {
//     const response = await api.get(`/tailor/result/${jobId}`);
//     return response;
//   } catch (error) {
//     throw handleApiError(error);
//   }
// };

// Save tailored resume
export const saveTailoredResume = async (jobId, saveOptions) => {
  try {
    const response = await api.post(`/tailor/save/${jobId}`, saveOptions);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Download tailored resume as PDF
export const downloadResumePdf = async (jobId) => {
  try {
    // Using a different axios configuration that supports blob responses
    const response = await api.get(`/tailor/download/${jobId}`, {
      responseType: 'blob'
    });
    
    // Create a download link and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'tailored-resume.pdf');
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    return true;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get user's tailoring history
// export const getTailoringHistory = async () => {
//   try {
//     const response = await api.get('/tailor/history');
//     return response;
//   } catch (error) {
//     throw handleApiError(error);
//   }
// };

// Get a specific tailored resume from history
export const getTailoredResumeById = async (resumeId) => {
  try {
    const response = await api.get(`/tailor/resume/${resumeId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Delete a tailored resume from history
export const deleteTailoredResume = async (resumeId) => {
  try {
    const response = await api.delete(`/tailor/resume/${resumeId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Handle API errors
// const handleApiError = (error) => {
//   if (error.response) {
//     // Server responded with an error status
//     const { data, status } = error.response;
    
//     return {
//       message: data.message || 'Request failed',
//       status,
//       data
//     };
//   } else if (error.request) {
//     // Request was made but no response received
//     return {
//       message: 'No response from server. Please check your connection.',
//       status: 0
//     };
//   } else {
//     // Something else happened while setting up the request
//     return {
//       message: error.message || 'An unexpected error occurred',
//       status: 0
//     };
//   }
// };

/**
 * Start a new tailoring session
 * @param {Object} sessionData - Session data (job_description, custom_input, resume_id, profile_id)
 * @returns {Promise<Object>} - Session response with session_id
 */
export const startTailoringSession = async (sessionData) => {
  try {
    const response = await api.post('/tailor/session/start', sessionData);
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get details of a specific tailoring session
 * @param {string} sessionId - Session ID
 * @returns {Promise<Object>} - Session details
 */
export const getTailoringSession = async (sessionId) => {
  try {
    const response = await api.get(`/tailor/session/${sessionId}`);
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Update multiple session properties at once
 * @param {string} sessionId - Session ID
 * @param {Object} updateData - Session properties to update
 * @returns {Promise<Object>} - Updated session
 */
export const updateSession = async (sessionId, updateData) => {
  try {
    const response = await api.patch(`/tailor/session/${sessionId}`, updateData);
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Process a tailoring session to generate a tailored resume
 * @param {string} sessionId - Session ID
 * @returns {Promise<Object>} - Processing response
 */
export const processTailoringSession = async (sessionId) => {
  try {
    const response = await api.post(`/tailor/session/${sessionId}/process`);
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get tailoring result for a processed session
 * @param {string} sessionId - Session ID
 * @returns {Promise<Object>} - Tailoring result
 */
export const getTailoringResult = async (sessionId) => {
  try {
    const response = await api.get(`/tailor/session/${sessionId}/result`);
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Download a tailored resume
 * @param {string} sessionId - Session ID
 * @param {string} format - Output format (pdf, docx, text)
 * @returns {Promise<Blob>} - File blob for download
 */
// export const downloadTailoredResume = async (sessionId, format = 'pdf') => {
//   try {
//     // Create request with proper content type expectations
//     const headers = {
//       'Accept': format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
//     };
    
//     // Add authorization headers if available
//     if (api.defaults.headers?.common?.Authorization) {
//       headers.Authorization = api.defaults.headers.common.Authorization;
//     }

//     // Use axios with arraybuffer response type
//     const response = await api.get(`/tailor/session/${sessionId}/download?format=${format}`, {
//       responseType: 'arraybuffer',
//       headers
//     });

//     // Safely access content type, with fallback
//     const contentType = response.headers?.['content-type'] || 
//       (format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');

//     // Create blob from arraybuffer
//     const blob = new Blob([response.data], { type: contentType });

//     // Default filename with session ID
//     let filename = `tailored_resume_${sessionId}.${format}`;
    
//     // Safely try to extract filename from headers if they exist
//     if (response.headers && response.headers['content-disposition']) {
//       const disposition = response.headers['content-disposition'];
//       const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
//       const matches = filenameRegex.exec(disposition);
//       if (matches && matches[1]) {
//         filename = matches[1].replace(/['"]/g, '');
//       }
//     }

//     // Create download URL and trigger download
//     const downloadUrl = window.URL.createObjectURL(blob);
//     const link = document.createElement('a');
//     link.href = downloadUrl;
//     link.setAttribute('download', filename);
//     document.body.appendChild(link);
//     link.click();

//     // Cleanup
//     setTimeout(() => {
//       if (document.body.contains(link)) {
//         document.body.removeChild(link);
//       }
//       window.URL.revokeObjectURL(downloadUrl);
//     }, 200);

//     // Log success
//     console.log('Download initiated with content type:', contentType);
//     return true;
//   } catch (error) {
//     console.error('Download failed:', error);
    
//     // Provide detailed diagnostics
//     if (error.response) {
//       console.error('Response status:', error.response.status);
//       if (error.response.data) {
//         try {
//           // Try to decode the error response
//           const decoder = new TextDecoder();
//           const errorText = decoder.decode(error.response.data);
//           console.error('Error response:', errorText);
//         } catch (e) {
//           console.error('Could not decode error response');
//         }
//       }
//     }
    
//     throw handleApiError(error);
//   }
// };

export const downloadTailoredResume = async (sessionId, format = 'pdf') => {
  try {
    // Get the base URL from your API if available
    // const baseUrl = api.defaults.API_URL || '';
    const url = `${api.API_URL}/tailor/session/${sessionId}/download?format=${format}`;
    
    // Get authorization token from api if available
    const token = localStorage.getItem('token');
    const headers = new Headers();
    headers.append('Authorization', `Bearer ${token}`);
    
    // Set proper accept header
    headers.append('Accept', format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
    
    // Make fetch request
    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include', // Include cookies if needed
    });
    
    // Check if response is ok
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    // Get blob directly from response
    const blob = await response.blob();
    
    // Get filename from headers or use default
    let filename = `tailored_resume_${sessionId}.${format}`;
    const disposition = response.headers.get('content-disposition');
    if (disposition) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches && matches[1]) {
        filename = matches[1].replace(/['"]/g, '');
      }
    }
    // Create download link
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    
    // Append to body, click and clean up
    document.body.appendChild(link);
    link.click();
    
    // Clean up after download
    setTimeout(() => {
      if (document.body.contains(link)) {
        document.body.removeChild(link);
      }
      window.URL.revokeObjectURL(downloadUrl);
    }, 200);
    
    return true;
  } catch (error) {
    console.error('Download failed:', error);
    
    // Try to provide additional error info
    if (error.response) {
      console.error('Response error:', error.response);
    }
    
    // Handle the error or rethrow
    throw error;
  }
};

/**
 * Get user profiles
 * @returns {Promise<Array>} - List of user profiles
 */
export const getUserProfiles = async () => {
  try {
    const response = await api.get('/profile/profiles');
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get user resumes
 * @returns {Promise<Array>} - List of user resumes
 */
export const getUserResumes = async () => {
  try {
    const response = await api.get('/profile/resumes');
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get tailoring history
 * @returns {Promise<Array>} - List of tailoring history items
 */
// In your services/tailor.js file
export const getTailoringHistory = async (params = {}) => {
    const { limit = 10, offset = 1 } = params;
    
    try {
        return await api.get('/tailor/session/list', { limit, offset });
    } catch (error) {
        console.error('Error fetching resume history:', error);
        throw error;
    }
};

/**
 * Get a specific tailoring result by ID
 * @param {number} historyId - History record ID
 * @returns {Promise<Object>} - Tailoring result
 */
export const getTailoringResultById = async (historyId) => {
  try {
    const response = await api.get(`/tailor/history/${historyId}`);
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Handle API errors
const handleApiError = (error) => {
  if (error.response) {
    // Server responded with an error status
    const { data, status } = error.response;
    
    return {
      message: data.message || 'Request failed',
      status,
      data
    };
  } else if (error.request) {
    // Request was made but no response received
    return {
      message: 'No response from server. Please check your connection.',
      status: 0
    };
  } else {
    // Something else happened while setting up the request
    return {
      message: error.message || 'An unexpected error occurred',
      status: 0
    };
  }
};

// Export all functions as a service object
export default {
  startTailoringSession,
  getTailoringSession,
  updateSession,
  processTailoringSession,
  getTailoringResult,
  downloadTailoredResume,
  getUserProfiles,
  getUserResumes,
  getTailoringHistory,
  getTailoringResultById,
  deleteTailoredResume,
  getTailoredResumeById,
  downloadResumePdf,
  saveTailoredResume,
  submitTailoringJob,
  getTailoringStatus,
};